import * as Sentry from '@sentry/react';
import { useState } from 'react';

import { AddFirmwareDialog } from '../components/firmwares/AddFirmwareDialog';
import { FirmwaresTable } from '../components/firmwares/FirmwaresTable';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui';
import { useCreateFirmware, useDeleteFirmware, useFirmwares, useIsAdmin, useMe } from '../hooks';
import { useIntl } from '../hooks/useIntl.js';
import type { Firmware } from '../types/firmware.js';
import type { FirmwareFormValues } from '../validations/firmware-form.js';

function AccessDenied() {
  const intl = useIntl();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          {intl.formatMessage({ id: 'firmwares.accessDenied.title' })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-gray-600">
          {intl.formatMessage({ id: 'firmwares.accessDenied.description' })}
        </p>
      </CardContent>
    </Card>
  );
}

export default function Firmwares() {
  const intl = useIntl();
  const { data: me, isLoading: isLoadingMe } = useMe();
  const isAdminUser = useIsAdmin(me);
  const {
    data: firmwares = [],
    isLoading: isLoadingFirmwares,
    isRefetching,
    error,
    refetch,
  } = useFirmwares({ enabled: isAdminUser });
  const createFirmware = useCreateFirmware();
  const deleteFirmwareMutation = useDeleteFirmware();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Firmware | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDialogOpenChange = (open: boolean) => {
    setIsDialogOpen(open);
  };

  const handleDialogSubmit = async (values: FirmwareFormValues): Promise<void | string> => {
    try {
      await createFirmware.mutateAsync({
        version: values.version.trim(),
        deviceModels: values.deviceModels,
        file: values.file,
      });
      setIsDialogOpen(false);
      return;
    } catch (error) {
      Sentry.captureException(error);
      return intl.formatMessage({ id: 'firmwares.error.failedToCreate' });
    }
  };

  if (isLoadingMe) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{intl.formatMessage({ id: 'firmwares.title' })}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-foreground">
            {intl.formatMessage({ id: 'firmwares.loadingAccount' })}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!me || !isAdminUser) {
    return <AccessDenied />;
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteError(null);
    try {
      await deleteFirmwareMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err) {
      Sentry.captureException(error);

      if (err instanceof Error) {
        setDeleteError(err.message);
      } else {
        setDeleteError(intl.formatMessage({ id: 'firmwares.failedToDelete' }));
      }
    }
  };

  const isDeleting = deleteFirmwareMutation.isPending;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              {intl.formatMessage({ id: 'firmwares.title' })}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                disabled={isLoadingFirmwares || isRefetching}
              >
                {isLoadingFirmwares || isRefetching
                  ? intl.formatMessage({ id: 'firmwares.refreshing' })
                  : intl.formatMessage({ id: 'firmwares.refresh' })}
              </Button>
              <Button size="sm" onClick={() => setIsDialogOpen(true)}>
                {intl.formatMessage({ id: 'firmwares.addFirmware' })}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-sm text-destructive">
              {intl.formatMessage({ id: 'firmwares.error.failedToLoad' }, { error: error.message })}
            </div>
          ) : isLoadingFirmwares ? (
            <div className="text-sm text-foreground">
              {intl.formatMessage({ id: 'firmwares.loading' })}
            </div>
          ) : (
            <FirmwaresTable
              firmwares={firmwares}
              onDelete={(firmware) => {
                setDeleteTarget(firmware);
                setDeleteError(null);
              }}
            />
          )}
        </CardContent>
      </Card>

      <AddFirmwareDialog
        open={isDialogOpen}
        isSubmitting={createFirmware.isPending}
        onOpenChange={handleDialogOpenChange}
        onSubmit={handleDialogSubmit}
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
            setDeleteError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{intl.formatMessage({ id: 'firmwares.confirmDelete' })}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground">
            {intl.formatMessage(
              { id: 'firmwares.confirmDeleteDesc' },
              { version: deleteTarget?.version ?? '' },
            )}
          </p>
          {deleteError && <div className="text-sm text-destructive">{deleteError}</div>}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDeleteTarget(null);
                setDeleteError(null);
              }}
              disabled={isDeleting}
            >
              {intl.formatMessage({ id: 'common.cancel' })}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting
                ? intl.formatMessage({ id: 'firmwares.deleting' })
                : intl.formatMessage({ id: 'common.delete' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
